import React from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from 'hooks/useAuth';
import { useLicenseStatusContext } from 'contexts/LicenseStatusContext';
import { ROUTES } from 'constants/index';
import { isTenantRole } from 'utils/roles';

interface LicenseGateProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  disabled?: boolean;
}
 
export const LicenseGate: React.FC<LicenseGateProps> = ({ 
  children, 
  fallback,
  disabled = false 
}) => {
  const { user } = useAuth();
  const { isExpired, hasLicense } = useLicenseStatusContext();
  const location = useLocation();
 
  const isTenantUser = isTenantRole(user?.roleId);
  const isOnDashboard = location.pathname === ROUTES.DASHBOARD;
  const isOnPricingPage = location.pathname === ROUTES.PRICING;
  const isOnUpgradePackagePage = location.pathname === ROUTES.UPGRADE_PACKAGE;
  const isOnRenewPackagePage = location.pathname === ROUTES.RENEW_PACKAGE;
  const isOnProfilePage =
    location.pathname === ROUTES.MY_PROFILE ||
    location.pathname.includes('/profile');
 
  if (isTenantUser && (!hasLicense || isExpired) && !isOnDashboard && !isOnPricingPage && !isOnUpgradePackagePage && !isOnRenewPackagePage && !isOnProfilePage) {
    if (fallback) {
      return <>{fallback}</>;
    }
     
    return (
      <div className="opacity-50 pointer-events-none relative">
        {children}
        <div className="absolute inset-0 bg-gray-100/50 flex items-center justify-center">
          <span className="text-sm text-gray-500 font-medium">
            {!hasLicense ? 'No license - Purchase required' : 'Subscription expired - Renewal required'}
          </span>
        </div>
      </div>
    );
  }
 
  if (disabled) {
    return (
      <div className="opacity-50 pointer-events-none">
        {children}
      </div>
    );
  }

  return <>{children}</>;
};
 
export const useLicenseGate = () => {
  const { user } = useAuth();
  const { isExpired, hasLicense } = useLicenseStatusContext();
  const location = useLocation();

  const isTenantUser = isTenantRole(user?.roleId);
  const isOnDashboard = location.pathname === ROUTES.DASHBOARD;
  const isOnPricingPage = location.pathname === ROUTES.PRICING;
  const isOnUpgradePackagePage = location.pathname === ROUTES.UPGRADE_PACKAGE;
  const isOnRenewPackagePage = location.pathname === ROUTES.RENEW_PACKAGE;
  const isOnProfilePage =
    location.pathname === ROUTES.MY_PROFILE ||
    location.pathname.includes('/profile');

  const shouldDisable = isTenantUser && (hasLicense && isExpired) && !isOnDashboard && !isOnPricingPage && !isOnUpgradePackagePage && !isOnRenewPackagePage && !isOnProfilePage;

  return {
    shouldDisable,
    /** Alias — hide create/save/edit actions when license blocks usage. */
    shouldHideActions: shouldDisable,
    isTenantUser,
    isExpired,
    hasLicense,
    isOnDashboard,
  };
};
